import * as React from 'react';
import {
  createViewConfig,
  view,
  useConstant,
  useIsomorphicLayoutEffect,
  useTokenValue,
} from '@sail/engine';
import {tokens} from '@sail/tokens/deprecated';
import {useResizeObserver} from '../hooks/useResizeObserver';
import {TabListStateContext} from './TabListStateContext';
import {useIsMounted} from '../../lib/useIsMounted';

export const TabLineConfig = createViewConfig({
  name: 'TabLine',
  props: {} as {measureOnMount?: boolean},
});

/**
 * For internal Sail use only.
 * Underlines the currently selected tab, animating transitions between them.
 */
export const TabLine = TabLineConfig.createView(
  ({measureOnMount = false, ...props}) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const state = React.useContext(TabListStateContext);
    const transitionDuration = parseInt(
      useTokenValue(tokens.animation.duration),
      10,
    );

    const animationDisabledTimeout = React.useRef<NodeJS.Timeout | undefined>(
      undefined,
    );

    // intentionally using style instead of CSS to avoid inserting a style rule per pixel shift
    const style = React.useRef({width: '', transform: ''});

    const isMountedRef = useIsMounted();

    const updateLinePosition = React.useCallback(() => {
      // find the selected tab from within the current container
      const selectedTab = ref.current?.parentElement?.querySelector(
        '[aria-selected="true"][data-key]:not([aria-hidden="true"])',
      ) as HTMLElement | undefined;

      if (selectedTab) {
        const updates = {transform: '', width: '', transition: ''};
        // eslint-disable-next-line @sail/ui-no-reflow
        const {offsetWidth, offsetLeft} = selectedTab;
        // Subtract tab padding so the underline aligns with the text, not
        // the padded element. Safe to call getComputedStyle here — the
        // offsetWidth/offsetLeft reads above already triggered layout, so
        // this reads from the cached computation.
        const isStretched = selectedTab.hasAttribute('data-stretched');
        // eslint-disable-next-line @sail/ui-no-reflow
        const tabStyle = isStretched ? null : getComputedStyle(selectedTab);
        const pl = tabStyle ? parseFloat(tabStyle.paddingLeft) || 0 : 0;
        const pr = tabStyle ? parseFloat(tabStyle.paddingRight) || 0 : 0;

        // Exclude paddingLeft from translateX on initial render to prevent flicker
        const paddingLeftOffset = isMountedRef.current ? pl : 0;

        updates.transform = `translateX(${offsetLeft + paddingLeftOffset}px)`;
        updates.width = `${offsetWidth - pl - pr}px`;

        if (
          ref.current &&
          (style.current.width !== updates.width ||
            style.current.transform !== updates.transform)
        ) {
          ref.current.style.setProperty('--tab-line-width', updates.width);
          ref.current.style.setProperty(
            '--tab-line-transform',
            updates.transform,
          );
        }
      }
    }, [style, isMountedRef]);

    const node = useConstant(() => ({
      get current() {
        return ref.current?.parentElement || null;
      },
    }));

    useIsomorphicLayoutEffect(() => {
      if (!isMountedRef.current) {
        ref.current?.style.setProperty('--tab-line-transition', 'none');

        updateLinePosition();

        setTimeout(() => {
          ref.current?.style.removeProperty('--tab-line-transition');
        }, 0);

        return;
      }

      setTimeout(() => updateLinePosition(), 0);
    }, [updateLinePosition, state?.selectedKey, isMountedRef]);

    useResizeObserver({
      node,
      handler: () => {
        ref.current?.style.setProperty('--tab-line-transition', 'none');

        updateLinePosition();

        clearTimeout(animationDisabledTimeout.current);
        animationDisabledTimeout.current = setTimeout(() => {
          ref.current?.style.removeProperty('--tab-line-transition');
        }, transitionDuration);
      },
      avoidInitialEvent: !measureOnMount,
    });

    return (
      <view.div
        role="presentation"
        {...props}
        css={{
          width: 'var(--tab-line-width)',
          transform: 'var(--tab-line-transform)',
          transition: `var(--tab-line-transition, transform ${transitionDuration}ms ${tokens.animation.easing}, width ${transitionDuration}ms ${tokens.animation.easing})`,
          position: 'absolute',
          bottom: '-1px',
          height: 'xxsmall',
          backgroundColor: 'action.primary',
        }}
        ref={ref}
      />
    );
  },
);
